import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Search, SlidersHorizontal, ArrowUpDown, Columns, Settings,
  X, Plus, ArrowUp, ArrowDown, GripVertical, Trash2,
  Type, Hash, Calendar, ToggleLeft, List, Link, Users, Clock, CheckSquare,
  Edit2, Copy,
} from 'lucide-react';
import { RecordSearchField, UserSearchField } from './CreateRecordModal';

const FIELD_TYPE_ICONS = {
  text: Type,
  number: Hash,
  date: Calendar,
  boolean: ToggleLeft,
  select: List,
  multi_select: CheckSquare,
  url: Link,
  object_relationship: Users,
  user: Users,
};

/* ─── Popover wrapper ─── */

function Popover({ trigger, open, onOpenChange, children, align = 'left' }) {
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target) &&
          !e.target.closest('[data-record-dropdown]')) {
        onOpenChange(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open, onOpenChange]);

  return (
    <div ref={ref} className="relative">
      {trigger}
      {open && (
        <div className={`absolute top-full mt-1.5 z-50 bg-white rounded-xl shadow-lg border border-gray-200 ${
          align === 'right' ? 'right-0' : 'left-0'
        }`}>
          {children}
        </div>
      )}
    </div>
  );
}

/* ─── Sort Popover ─── */

function SortPopover({ sortBy, sortOrder, onSort, activeView }) {
  const [open, setOpen] = useState(false);

  const sortableFields = useMemo(() => {
    const fields = [];
    // View columns first
    if (activeView?.columns) {
      for (const col of activeView.columns) {
        const key = col.field_definition_id || col.system_field;
        fields.push({ key, label: col.label, type: col.system_field ? 'date' : null });
      }
    }
    // System fields
    if (!fields.some((f) => f.key === 'created_at')) {
      fields.push({ key: 'created_at', label: 'Created', type: 'date' });
    }
    if (!fields.some((f) => f.key === 'updated_at')) {
      fields.push({ key: 'updated_at', label: 'Updated', type: 'date' });
    }
    return fields;
  }, [activeView]);

  const activeLabel = sortBy
    ? sortableFields.find((f) => f.key === sortBy)?.label || 'Sort'
    : null;

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger={
        <button
          onClick={() => setOpen(!open)}
          className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm rounded-lg border transition-colors ${
            sortBy
              ? 'border-brand-200 bg-brand-50 text-brand-700'
              : 'border-gray-200 text-gray-600 hover:bg-gray-50'
          }`}
        >
          <ArrowUpDown className="w-3.5 h-3.5" />
          <span>{activeLabel || 'Sort'}</span>
          {sortBy && (
            <button
              onClick={(e) => { e.stopPropagation(); onSort(null, null); }}
              className="ml-0.5 p-0.5 hover:bg-brand-100 rounded"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </button>
      }
    >
      <div className="w-56 py-1">
        <p className="px-3 py-1.5 text-[11px] font-medium text-gray-400 uppercase tracking-wider">Sort by</p>
        {sortableFields.map((field) => (
          <div key={field.key} className="flex items-center">
            <button
              onClick={() => {
                const newOrder = sortBy === field.key && sortOrder === 'asc' ? 'desc' : 'asc';
                onSort(field.key, newOrder);
                setOpen(false);
              }}
              className={`flex-1 flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 transition-colors text-left ${
                sortBy === field.key ? 'text-brand-700 font-medium' : 'text-gray-700'
              }`}
            >
              <span className="flex-1">{field.label}</span>
              {sortBy === field.key && (
                sortOrder === 'asc'
                  ? <ArrowUp className="w-3.5 h-3.5 text-brand-500" />
                  : <ArrowDown className="w-3.5 h-3.5 text-brand-500" />
              )}
            </button>
          </div>
        ))}
      </div>
    </Popover>
  );
}

/* ─── Filter Popover ─── */

function FilterPopover({ filters, onFiltersChange, availableFields }) {
  const [open, setOpen] = useState(false);
  const [rules, setRules] = useState([]);

  // Sync rules from filters prop only when popover opens
  useEffect(() => {
    if (!open) return;
    const r = Object.entries(filters || {})
      .filter(([_, v]) => v !== undefined && v !== '')
      .map(([fieldId, value]) => ({ fieldId, operator: 'is', value }));
    setRules(r);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const filterableFields = useMemo(() => {
    return availableFields.filter((f) =>
      ['select', 'text', 'boolean', 'number', 'date', 'multi_select', 'object_relationship', 'user'].includes(f.field_type)
    );
  }, [availableFields]);

  const usedFieldIds = rules.map((r) => r.fieldId);
  const unusedFields = filterableFields.filter((f) => !usedFieldIds.includes(f.id));

  const addRule = (fieldId) => {
    setRules((prev) => [...prev, { fieldId, operator: 'is', value: '' }]);
  };

  const updateRule = (index, value) => {
    const newRules = rules.map((r, i) => i === index ? { ...r, value } : r);
    setRules(newRules);
    applyRules(newRules);
  };

  const removeRule = (index) => {
    const newRules = rules.filter((_, i) => i !== index);
    setRules(newRules);
    applyRules(newRules);
  };

  const applyRules = (r) => {
    const newFilters = {};
    for (const rule of r) {
      if (rule.value !== '' && rule.value !== undefined) {
        newFilters[rule.fieldId] = rule.value;
      }
    }
    onFiltersChange(newFilters);
  };

  const clearAll = () => {
    setRules([]);
    onFiltersChange({});
  };

  // Derive from filters prop so the badge is correct even before the popover opens
  const activeCount = Object.values(filters || {}).filter((v) => v !== undefined && v !== '').length;

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger={
        <button
          onClick={() => setOpen(!open)}
          className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm rounded-lg border transition-colors ${
            activeCount > 0
              ? 'border-brand-200 bg-brand-50 text-brand-700'
              : 'border-gray-200 text-gray-600 hover:bg-gray-50'
          }`}
        >
          <SlidersHorizontal className="w-3.5 h-3.5" />
          <span>Filter</span>
          {activeCount > 0 && (
            <>
              <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-brand-600 text-white text-[10px] font-medium">
                {activeCount}
              </span>
              <button
                onClick={(e) => { e.stopPropagation(); clearAll(); }}
                className="ml-0.5 p-0.5 hover:bg-brand-100 rounded"
              >
                <X className="w-3 h-3" />
              </button>
            </>
          )}
        </button>
      }
    >
      <div className="w-96 py-2">
        {rules.length === 0 ? (
          <p className="px-3 py-3 text-sm text-gray-400 text-center">No active filters</p>
        ) : (
          <div className="space-y-2 px-3 pb-2">
            {rules.map((rule, index) => {
              const field = availableFields.find((f) => f.id === rule.fieldId);
              if (!field) return null;
              return (
                <FilterRule
                  key={`${rule.fieldId}-${index}`}
                  field={field}
                  value={rule.value}
                  onChange={(val) => updateRule(index, val)}
                  onRemove={() => removeRule(index)}
                />
              );
            })}
          </div>
        )}

        {/* Add filter */}
        {unusedFields.length > 0 && (
          <div className="border-t border-gray-100 pt-1 mt-1">
            <AddFilterDropdown fields={unusedFields} onAdd={addRule} />
          </div>
        )}
      </div>
    </Popover>
  );
}

function FilterRule({ field, value, onChange, onRemove }) {
  const IconComp = FIELD_TYPE_ICONS[field.field_type] || Type;
  const inputClasses = "w-full px-2 py-1 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-brand-500/30 focus:border-brand-500";

  const renderInput = () => {
    switch (field.field_type) {
      case 'select':
        return (
          <select value={value || ''} onChange={(e) => onChange(e.target.value)} className={inputClasses}>
            <option value="">Any</option>
            {(field.options?.choices || []).map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        );
      case 'boolean':
        return (
          <select
            value={value ?? ''}
            onChange={(e) => {
              const v = e.target.value === '' ? undefined : e.target.value === 'true';
              onChange(v);
            }}
            className={inputClasses}
          >
            <option value="">Any</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        );
      case 'object_relationship':
        return (
          <div className="[&>div]:!py-1 [&>div]:!px-2 [&>div]:!text-xs [&>div]:!rounded-md [&>div]:!border-gray-200">
            <RecordSearchField
              objectDefinitionId={field.options?.related_object_definition_id}
              value={value || ''}
              onChange={(v) => onChange(v)}
              placeholder="Select..."
            />
          </div>
        );
      case 'user':
        return (
          <div className="[&>select]:!py-1 [&>select]:!px-2 [&>select]:!text-xs [&>select]:!rounded-md [&>select]:!border-gray-200">
            <UserSearchField
              value={value || ''}
              onChange={(v) => onChange(v)}
              placeholder="Select user..."
            />
          </div>
        );
      default:
        return (
          <input
            type={field.field_type === 'number' ? 'number' : field.field_type === 'date' ? 'date' : 'text'}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Value..."
            className={`${inputClasses} placeholder:text-gray-400`}
          />
        );
    }
  };

  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-1.5 min-w-0 flex-shrink-0">
        <IconComp className="w-3 h-3 text-gray-400" />
        <span className="text-xs font-medium text-gray-600 truncate max-w-[80px]">{field.name}</span>
      </div>
      <span className="text-xs text-gray-400 flex-shrink-0">is</span>
      <div className="flex-1 min-w-0">
        {renderInput()}
      </div>
      <button
        onClick={onRemove}
        className="p-1 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}

function AddFilterDropdown({ fields, onAdd }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors"
      >
        <Plus className="w-3 h-3" />
        Add filter
      </button>
      {open && (
        <div className="absolute bottom-full left-0 mb-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-y-auto z-10">
          {fields.map((field) => {
            const IconComp = FIELD_TYPE_ICONS[field.field_type] || Type;
            return (
              <button
                key={field.id}
                onClick={() => { onAdd(field.id); setOpen(false); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 transition-colors text-left"
              >
                <IconComp className="w-3 h-3 text-gray-400" />
                {field.name}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ─── Columns Popover ─── */

function ColumnsPopover({ columns, availableFields, onColumnsChange }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('order'); // 'order' | 'add'
  const [search, setSearch] = useState('');
  const [dragIndex, setDragIndex] = useState(null);

  // "Add" tab: only show fields NOT already added
  const unusedFields = useMemo(() => {
    const usedFieldIds = new Set(columns.map((c) => c.field_definition_id).filter(Boolean));
    let fields = availableFields.filter((f) => !usedFieldIds.has(f.id));
    if (search.trim()) {
      const q = search.toLowerCase();
      fields = fields.filter((f) => f.name.toLowerCase().includes(q));
    }
    return fields;
  }, [availableFields, columns, search]);

  const unusedSystem = useMemo(() => {
    const systemFields = [
      { system_field: 'created_at', label: 'Created' },
      { system_field: 'updated_at', label: 'Updated' },
    ];
    const usedSys = new Set(columns.map((c) => c.system_field).filter(Boolean));
    let fields = systemFields.filter((sf) => !usedSys.has(sf.system_field));
    if (search.trim()) {
      const q = search.toLowerCase();
      fields = fields.filter((sf) => sf.label.toLowerCase().includes(q));
    }
    return fields;
  }, [columns, search]);

  const addField = (field) => {
    onColumnsChange([...columns, {
      field_definition_id: field.id,
      system_field: null,
      label: field.name,
      sort_order: columns.length,
      width: null,
    }]);
  };

  const addSystem = (sf) => {
    onColumnsChange([...columns, {
      field_definition_id: null,
      system_field: sf.system_field,
      label: sf.label,
      sort_order: columns.length,
      width: null,
    }]);
  };

  const handleDragStart = (index) => setDragIndex(index);
  const handleDragOver = (e, overIndex) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === overIndex) return;
    const next = [...columns];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(overIndex, 0, moved);
    onColumnsChange(next);
    setDragIndex(overIndex);
  };
  const handleDragEnd = () => setDragIndex(null);

  const totalUnused = unusedFields.length + unusedSystem.length;

  return (
    <Popover
      open={open}
      onOpenChange={(v) => { setOpen(v); if (!v) { setTab('order'); setSearch(''); } }}
      trigger={
        <button
          onClick={() => setOpen(!open)}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
        >
          <Columns className="w-3.5 h-3.5" />
          <span>Columns</span>
        </button>
      }
    >
      <div className="w-72">
        {/* Tabs */}
        <div className="flex border-b border-gray-100">
          <button
            onClick={() => { setTab('order'); setSearch(''); }}
            className={`flex-1 px-3 py-2 text-xs font-medium transition-colors ${
              tab === 'order'
                ? 'text-gray-900 border-b-2 border-gray-900'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Order ({columns.length})
          </button>
          <button
            onClick={() => setTab('add')}
            className={`flex-1 px-3 py-2 text-xs font-medium transition-colors ${
              tab === 'add'
                ? 'text-gray-900 border-b-2 border-gray-900'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Add field
          </button>
        </div>

        {tab === 'order' ? (
          /* ── Order tab: reorder + remove ── */
          <div className="py-1.5 px-2">
            {columns.length === 0 ? (
              <p className="px-1 py-4 text-xs text-gray-400 text-center">
                No columns yet.{' '}
                <button onClick={() => setTab('add')} className="text-brand-600 hover:underline">Add fields</button>
              </p>
            ) : (
              <div className="space-y-0.5 max-h-64 overflow-y-auto">
                {columns.map((col, index) => {
                  const field = availableFields.find((f) => f.id === col.field_definition_id);
                  const IconComp = col.system_field ? Clock : (field && FIELD_TYPE_ICONS[field.field_type]) || Type;
                  return (
                    <div
                      key={`${col.field_definition_id || col.system_field}-${index}`}
                      draggable
                      onDragStart={() => handleDragStart(index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragEnd={handleDragEnd}
                      className={`flex items-center gap-1.5 px-1.5 py-1.5 rounded-md text-xs transition-colors ${
                        dragIndex === index ? 'bg-brand-50' : 'hover:bg-gray-50'
                      }`}
                    >
                      <GripVertical className="w-3 h-3 text-gray-300 cursor-grab flex-shrink-0" />
                      <IconComp className="w-3 h-3 text-gray-400 flex-shrink-0" />
                      <span className="flex-1 text-gray-700 truncate">{col.label}</span>
                      <button
                        onClick={() => onColumnsChange(columns.filter((_, i) => i !== index))}
                        className="p-0.5 text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          /* ── Add tab: search + click to add ── */
          <div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search fields..."
                autoFocus
                className="w-full pl-8 pr-3 py-2 text-xs border-b border-gray-100 focus:outline-none placeholder:text-gray-400"
              />
            </div>
            <div className="max-h-56 overflow-y-auto py-1">
              {unusedFields.map((field) => {
                const IconComp = FIELD_TYPE_ICONS[field.field_type] || Type;
                return (
                  <button
                    key={field.id}
                    onClick={() => addField(field)}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 transition-colors text-left"
                  >
                    <Plus className="w-3 h-3 text-gray-400 flex-shrink-0" />
                    <IconComp className="w-3 h-3 text-gray-400 flex-shrink-0" />
                    <span>{field.name}</span>
                  </button>
                );
              })}
              {unusedSystem.length > 0 && (
                <>
                  {unusedFields.length > 0 && <div className="border-t border-gray-100 my-1" />}
                  <p className="px-3 py-0.5 text-[10px] font-medium text-gray-400 uppercase tracking-wider">System</p>
                  {unusedSystem.map((sf) => (
                    <button
                      key={sf.system_field}
                      onClick={() => addSystem(sf)}
                      className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 transition-colors text-left"
                    >
                      <Plus className="w-3 h-3 text-gray-400 flex-shrink-0" />
                      <Clock className="w-3 h-3 text-gray-400 flex-shrink-0" />
                      <span>{sf.label}</span>
                    </button>
                  ))}
                </>
              )}
              {totalUnused === 0 && (
                <p className="px-3 py-4 text-xs text-gray-400 text-center">
                  {search.trim() ? 'No fields match your search.' : 'All fields are already added.'}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </Popover>
  );
}

/* ─── View Settings Menu ─── */

function ViewSettingsMenu({ onEdit, onDuplicate, onDelete, viewName }) {
  const [open, setOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  return (
    <Popover
      open={open}
      onOpenChange={(v) => { setOpen(v); if (!v) setConfirmDelete(false); }}
      align="right"
      trigger={
        <button
          onClick={() => setOpen(!open)}
          className="inline-flex items-center gap-1.5 px-2 py-1.5 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
        >
          <Settings className="w-3.5 h-3.5" />
        </button>
      }
    >
      <div className="w-44 py-1">
        {confirmDelete ? (
          <div className="px-3 py-2">
            <p className="text-xs text-gray-600 mb-2">Delete "{viewName}"?</p>
            <div className="flex gap-2">
              <button
                onClick={() => { onDelete(); setOpen(false); setConfirmDelete(false); }}
                className="px-2.5 py-1 text-xs font-medium text-white bg-red-600 rounded hover:bg-red-700"
              >
                Delete
              </button>
              <button
                onClick={() => setConfirmDelete(false)}
                className="px-2.5 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded hover:bg-gray-200"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            <button
              onClick={() => { onEdit(); setOpen(false); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left"
            >
              <Edit2 className="w-3.5 h-3.5 text-gray-400" />
              Edit view
            </button>
            <button
              onClick={() => { onDuplicate(); setOpen(false); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left"
            >
              <Copy className="w-3.5 h-3.5 text-gray-400" />
              Duplicate view
            </button>
            <div className="border-t border-gray-100 my-1" />
            <button
              onClick={() => setConfirmDelete(true)}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors text-left"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Delete view
            </button>
          </>
        )}
      </div>
    </Popover>
  );
}

/* ─── Main Toolbar ─── */

export default function ViewToolbar({
  query,
  setQuery,
  objectName,
  sortBy,
  sortOrder,
  onSort,
  activeView,
  availableFields,
  isKanban,
  onColumnsChange,
  onFiltersChange,
  onEditView,
  onDuplicateView,
  onDeleteView,
}) {
  const columns = activeView?.columns || [];
  const filters = activeView?.filters || {};

  return (
    <div className="flex items-center gap-2 mb-4">
      {/* Search */}
      <div className="relative flex-1 max-w-xs">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
        <input
          type="text"
          placeholder={`Search ${objectName.toLowerCase()}...`}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 placeholder:text-gray-400 transition-colors"
        />
      </div>

      {/* Toolbar buttons */}
      <div className="flex items-center gap-1.5">
        <FilterPopover
          filters={filters}
          onFiltersChange={onFiltersChange}
          availableFields={availableFields}
        />

        <SortPopover
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={onSort}
          activeView={activeView}
        />

        {!isKanban && (
          <ColumnsPopover
            columns={columns}
            availableFields={availableFields}
            onColumnsChange={onColumnsChange}
          />
        )}

        {activeView && (
          <ViewSettingsMenu
            viewName={activeView.name}
            onEdit={onEditView}
            onDuplicate={onDuplicateView}
            onDelete={onDeleteView}
          />
        )}
      </div>
    </div>
  );
}
